<script setup>
import { ref, computed } from 'vue';

defineOptions({ name: 'ApiResponseTree' });

const props = defineProps({
  data: {
    type: [Object, Array, String, Number, Boolean],
    default: null,
  },
  prefix: {
    type: String,
    default: 'api',
  },
  isNested: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['pick']);

const viewMode = ref('flat');
const search = ref('');
const expanded = ref({});

const buildToken = path => `{{${path}}}`;

function detectKind(value) {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  return 'string';
}

function preview(value) {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return `[${value.length}]`;
  if (typeof value === 'object') return `{${Object.keys(value).length}}`;
  const s = String(value);
  return s.length > 40 ? `${s.slice(0, 37)}…` : s;
}

const MAX_PATHS = 2000;
const MAX_DEPTH = 12;

function collectPaths(root, rootPath) {
  const acc = [];
  if (!root || typeof root !== 'object') return acc;
  const seen = new WeakSet();
  const stack = [{ node: root, path: rootPath, depth: 0 }];
  while (stack.length && acc.length < MAX_PATHS) {
    const { node, path, depth } = stack.pop();
    if (!node || typeof node !== 'object') continue;
    if (seen.has(node)) continue;
    seen.add(node);
    if (depth >= MAX_DEPTH) continue;
    const entries = Array.isArray(node)
      ? node.map((v, i) => [String(i), v])
      : Object.entries(node);
    // Reverse to keep natural order after pop()
    for (let i = entries.length - 1; i >= 0; i -= 1) {
      const [key, value] = entries[i];
      const childPath = `${path}.${key}`;
      acc.push({
        path: childPath,
        relative: childPath.startsWith(`${rootPath}.`)
          ? childPath.slice(rootPath.length + 1)
          : childPath,
        depth: depth + 1,
        kind: detectKind(value),
        preview: preview(value),
        value,
      });
      if (value && typeof value === 'object') {
        stack.push({ node: value, path: childPath, depth: depth + 1 });
      }
    }
  }
  return acc;
}

const allPaths = computed(() => {
  try {
    return collectPaths(props.data, props.prefix);
  } catch (err) {
    console.error('[ApiResponseTree.collectPaths] failed', err);
    return [];
  }
});

const filteredPaths = computed(() => {
  if (!search.value.trim()) return allPaths.value;
  const q = search.value.trim().toLowerCase();
  return allPaths.value.filter(entry => {
    if (entry.relative.toLowerCase().includes(q)) return true;
    if (entry.preview.toLowerCase().includes(q)) return true;
    return false;
  });
});

function pick(entry) {
  emit('pick', { token: buildToken(entry.path), path: entry.path });
}

function pickCollectAll(entry) {
  emit('pick', { token: buildToken(`${entry.path}.*`), path: `${entry.path}.*` });
}

const treeEntries = computed(() => buildTree(props.data, props.prefix));

function buildTree(node, path) {
  if (Array.isArray(node)) {
    return node.map((item, idx) => ({
      key: String(idx),
      path: `${path}.${idx}`,
      value: item,
      kind: detectKind(item),
      children: buildTree(item, `${path}.${idx}`),
      isArrayItem: true,
    }));
  }
  if (node && typeof node === 'object') {
    return Object.entries(node).map(([key, value]) => ({
      key,
      path: `${path}.${key}`,
      value,
      kind: detectKind(value),
      children: buildTree(value, `${path}.${key}`),
    }));
  }
  return [];
}

const isOpen = path => expanded.value[path] !== false;
const toggle = path => {
  expanded.value = { ...expanded.value, [path]: !isOpen(path) };
};

const hasSample = computed(() => {
  if (props.data === null || props.data === undefined) return false;
  if (Array.isArray(props.data)) return props.data.length > 0;
  if (typeof props.data === 'object') return Object.keys(props.data).length > 0;
  return true;
});
</script>

<template>
  <div
    class="text-xs font-mono"
    :class="{ 'space-y-2': !isNested }"
  >
    <div
      v-if="!isNested && !hasSample"
      class="text-slate-500 dark:text-slate-400 italic p-2"
    >
      {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NO_SAMPLE', 'Nenhuma amostra de resposta. Clique em Testar API para capturar.') }}
    </div>
    <template v-else-if="!isNested">
      <div class="flex items-center gap-1">
        <input
          v-model="search"
          type="text"
          placeholder="Buscar campo, ex: nome, lider.telefone, endereco..."
          class="flex-1 px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 font-sans"
        />
        <div class="inline-flex rounded border border-slate-300 dark:border-slate-600 overflow-hidden text-[11px]">
          <button
            type="button"
            class="px-2 py-1"
            :class="viewMode === 'flat'
              ? 'bg-woot-500 text-white'
              : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300'"
            title="Lista plana (todos os caminhos)"
            @click="viewMode = 'flat'"
          >
            <span class="i-lucide-list w-3 h-3" />
          </button>
          <button
            type="button"
            class="px-2 py-1"
            :class="viewMode === 'tree'
              ? 'bg-woot-500 text-white'
              : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300'"
            title="Árvore expansível"
            @click="viewMode = 'tree'"
          >
            <span class="i-lucide-git-branch w-3 h-3" />
          </button>
        </div>
      </div>

      <ul
        v-if="viewMode === 'flat'"
        class="space-y-0.5 max-h-72 overflow-auto"
      >
        <li
          v-if="!filteredPaths.length"
          class="text-slate-500 dark:text-slate-400 italic py-2"
        >
          Nenhum campo encontrado.
        </li>
        <li
          v-for="entry in filteredPaths"
          :key="entry.path"
          class="group"
        >
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="flex-1 flex items-center gap-2 px-2 py-1 rounded hover:bg-woot-50 dark:hover:bg-slate-700 text-left min-w-0"
              :title="entry.path"
              @click="pick(entry)"
            >
              <span class="text-purple-600 dark:text-purple-400 font-semibold truncate">{{ entry.relative }}</span>
              <span class="text-slate-400">:</span>
              <span
                class="truncate"
                :class="{
                  'text-emerald-600 dark:text-emerald-400': entry.kind === 'string',
                  'text-amber-600 dark:text-amber-400': entry.kind === 'number' || entry.kind === 'boolean',
                  'text-slate-400 italic': entry.kind === 'null',
                  'text-slate-500 dark:text-slate-300': ['array', 'object'].includes(entry.kind),
                }"
              >{{ entry.preview }}</span>
              <span class="ml-auto text-[10px] opacity-0 group-hover:opacity-100 text-woot-600 dark:text-woot-400 shrink-0">
                {{ $t('OMNI.SLOTS.API_AUTOMATIONS.INSERT', 'Inserir') }}
              </span>
            </button>
            <button
              v-if="entry.kind === 'array'"
              type="button"
              class="text-[10px] px-1 py-0.5 rounded border border-woot-300 dark:border-woot-700 text-woot-600 dark:text-woot-400 hover:bg-woot-50 dark:hover:bg-woot-900/20 shrink-0"
              title="Coletar todos os itens"
              @click="pickCollectAll(entry)"
            >
              *
            </button>
          </div>
        </li>
      </ul>

      <ul
        v-else
        class="space-y-0.5 max-h-72 overflow-auto"
      >
        <li
          v-for="node in treeEntries"
          :key="node.path"
        >
          <div class="flex items-center gap-1 group">
            <button
              v-if="node.children.length"
              type="button"
              class="w-4 h-4 inline-flex items-center justify-center text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
              @click="toggle(node.path)"
            >
              <span :class="isOpen(node.path) ? 'i-lucide-chevron-down w-3 h-3' : 'i-lucide-chevron-right w-3 h-3'" />
            </button>
            <span
              v-else
              class="w-4 inline-block"
            />
            <button
              type="button"
              class="flex-1 flex items-center gap-2 px-2 py-0.5 rounded hover:bg-woot-50 dark:hover:bg-slate-700 text-left min-w-0"
              @click="pick({ path: node.path })"
            >
              <span class="text-purple-600 dark:text-purple-400 font-semibold truncate">{{ node.key }}</span>
              <span class="text-slate-400">:</span>
              <span
                class="truncate"
                :class="{
                  'text-emerald-600 dark:text-emerald-400': node.kind === 'string',
                  'text-amber-600 dark:text-amber-400': node.kind === 'number' || node.kind === 'boolean',
                  'text-slate-400 italic': node.kind === 'null',
                  'text-slate-500 dark:text-slate-300': ['array', 'object'].includes(node.kind),
                }"
              >{{ preview(node.value) }}</span>
              <span class="ml-auto text-[10px] opacity-0 group-hover:opacity-100 text-woot-600 dark:text-woot-400 shrink-0">
                {{ $t('OMNI.SLOTS.API_AUTOMATIONS.INSERT', 'Inserir') }}
              </span>
            </button>
            <button
              v-if="node.kind === 'array'"
              type="button"
              class="text-[10px] px-1 py-0.5 rounded border border-woot-300 dark:border-woot-700 text-woot-600 dark:text-woot-400 hover:bg-woot-50 dark:hover:bg-woot-900/20 shrink-0"
              title="Coletar todos os itens"
              @click="pickCollectAll({ path: node.path })"
            >
              *
            </button>
          </div>
          <div
            v-if="node.children.length && isOpen(node.path)"
            class="pl-4 border-l border-slate-200 dark:border-slate-700 ml-2"
          >
            <ApiResponseTree
              :data="node.value"
              :prefix="node.path"
              is-nested
              @pick="$emit('pick', $event)"
            />
          </div>
        </li>
      </ul>
    </template>

    <!-- Nested (tree mode recursive) rendering -->
    <ul
      v-else
      class="space-y-0.5"
    >
      <li
        v-for="node in treeEntries"
        :key="node.path"
      >
        <div class="flex items-center gap-1 group">
          <button
            v-if="node.children.length"
            type="button"
            class="w-4 h-4 inline-flex items-center justify-center text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
            @click="toggle(node.path)"
          >
            <span :class="isOpen(node.path) ? 'i-lucide-chevron-down w-3 h-3' : 'i-lucide-chevron-right w-3 h-3'" />
          </button>
          <span
            v-else
            class="w-4 inline-block"
          />
          <button
            type="button"
            class="flex-1 flex items-center gap-2 px-2 py-0.5 rounded hover:bg-woot-50 dark:hover:bg-slate-700 text-left min-w-0"
            @click="pick({ path: node.path })"
          >
            <span class="text-purple-600 dark:text-purple-400 font-semibold truncate">{{ node.key }}</span>
            <span class="text-slate-400">:</span>
            <span
              class="truncate"
              :class="{
                'text-emerald-600 dark:text-emerald-400': node.kind === 'string',
                'text-amber-600 dark:text-amber-400': node.kind === 'number' || node.kind === 'boolean',
                'text-slate-400 italic': node.kind === 'null',
                'text-slate-500 dark:text-slate-300': ['array', 'object'].includes(node.kind),
              }"
            >{{ preview(node.value) }}</span>
            <span class="ml-auto text-[10px] opacity-0 group-hover:opacity-100 text-woot-600 dark:text-woot-400 shrink-0">
              {{ $t('OMNI.SLOTS.API_AUTOMATIONS.INSERT', 'Inserir') }}
            </span>
          </button>
          <button
            v-if="node.kind === 'array'"
            type="button"
            class="text-[10px] px-1 py-0.5 rounded border border-woot-300 dark:border-woot-700 text-woot-600 dark:text-woot-400 hover:bg-woot-50 dark:hover:bg-woot-900/20 shrink-0"
            title="Coletar todos os itens"
            @click="pickCollectAll({ path: node.path })"
          >
            *
          </button>
        </div>
        <div
          v-if="node.children.length && isOpen(node.path)"
          class="pl-4 border-l border-slate-200 dark:border-slate-700 ml-2"
        >
          <ApiResponseTree
            :data="node.value"
            :prefix="node.path"
            is-nested
            @pick="$emit('pick', $event)"
          />
        </div>
      </li>
    </ul>
  </div>
</template>
